import React, { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { useWatch } from "react-hook-form";
import { Box, Fade } from "@mui/material";

//** NDBX Components */
import {
  RHFCheckbox,
  Section,
  OneColumnStack,
  RHFTextField,
  Button,
  NotificationCard,
} from "@arp/react-ndbx";

//** Config */
import { policyOutcomeFieldNames } from "../../../config/pageInfo/pageFieldName.js";
import { policyOutcomeDisclaimers } from "../../../config/disclaimers/disclaimers.jsx";

//** Hooks */
import { useUpgradePolicyMutation } from "../../../hooks/dataHooks/useUpgradePolicyMutation.js";
import usePreQualification from "../../../hooks/usePreQualification.ts";

//----------------------------------------------------------------

export default function UpgradePolicy(props) {
  const {
    dataSet,
    setUpgradeSuccess,
    setUpgradeChecked,
    person = "personOne",
    accessToken,
    isRetrievePolicyExpired = false,
  } = { ...props };

  const [checkboxChecked, setCheckboxChecked] = useState(false);
  const [errorState, setErrorState] = useState(false);
  const [successState, setSuccessState] = useState(false);
  const [showButton, setShowButton] = useState(true); //* This gets set after we get a response from the API or the user clicks in the text field
  const [enableSaveButton, setEnableSaveButton] = useState(false);
  const { handleInputChange } = usePreQualification();
  const {
    methods: [methods],
    isDirty: [isDirty],
    isValid: [isValid],
    errors: [...errors],
    handleCCUpgradePolicy: [handleCCUpgradePolicy],
  } = useOutletContext();

  const { control, resetField, trigger } = { ...methods };

  const {
    mutate: upgradePolicyMutate,
    data: upgradePolicyData,
    isSuccess: isUpgradePolicySuccess,
    isLoading: isUpgradePolicyLoading,
    isError,
    error: mutError,
  } = useUpgradePolicyMutation();

  useEffect(() => {
    if (!isUpgradePolicySuccess) return;
    const statusUpdate = upgradePolicyData?.status === 200 ? true : false;
    setErrorState(!statusUpdate);
    setSuccessState(statusUpdate);
    setUpgradeSuccess(statusUpdate);
    setShowButton(false);
  }, [upgradePolicyData, isUpgradePolicySuccess]);

  useEffect(() => {
    resetAllFields();
  }, []);

  const resetAllFields = () => {
    setErrorState(false);
    setSuccessState(false);
    setShowButton(true);
    resetField(policyOutcomeFieldNames(person).EXISTING_POLICY_NUMBER, {
      defaultValue: "",
    });
  };

  const handleOnChange = () => {
    // Show the upgrade field
    setCheckboxChecked(!checkboxChecked);
    setUpgradeChecked(!checkboxChecked);
    resetAllFields();
    handleCCUpgradePolicy(!checkboxChecked);
    // manually update the preQual object
  };

  const handleInputOnFocus = (e) => {
    e.preventDefault();
    // const { name, value } = e?.target;
    if (errorState === true) {
      resetAllFields();
    }
  };

  const policyNumberWatch = useWatch({
    control,
    name: policyOutcomeFieldNames(person).EXISTING_POLICY_NUMBER,
  });

  useEffect(() => {
    if (policyNumberWatch?.length <= 0) {
      resetAllFields();
    }
    policyNumberWatch?.length >= 6
      ? setEnableSaveButton(true)
      : setEnableSaveButton(false);
  }, [policyNumberWatch]);

  //* Variable to ensure we can only click the upgrade button once.
  let disablePolicyUpgradeButton = false;

  const handleOnSave = (e) => {
    //* Disable the button once it is clicked.
    e.currentTarget.disabled = true;
    //* Call the API Mutation
    e.preventDefault();

    disablePolicyUpgradeButton = true;

    if (policyNumberWatch == null || policyNumberWatch?.length <= 0) {
      disablePolicyUpgradeButton = false;
      return;
    }
    const policyNumber = policyNumberWatch.trim();
    upgradePolicyMutate({
      policyNumber,
      accessToken,
    });
    handleInputChange({
      name: "existingPolicyNumber",
      value: policyNumber,
    });
    disablePolicyUpgradeButton = false;
  };

  return (
    <>
      <OneColumnStack maxWidth="100%" mb={0}>
        <hr />

        <Section ml={-2}>
          <RHFCheckbox
            label="Upgrade accepted"
            defaultChecked={dataSet?.policyOutcomeAcceptUpgrade}
            name={policyOutcomeFieldNames(person).ACCEPT_UPGRADE}
            onChange={handleOnChange}
            ml={-100}
            sx={{ ml: -100 }}
          ></RHFCheckbox>
        </Section>
      </OneColumnStack>

      <Fade in={checkboxChecked} timeout={500}>
        <Box
          sx={{
            display: checkboxChecked ? "flex" : "none",
            flexDirection: "column",
            outline: "0px solid black",
          }}
        >
          <OneColumnStack
            maxWidth="100%"
            data-identifier="accept-upgrade-stack"
          >
            <Section>
              <RHFTextField
                className="subvariant-text-input"
                variant="standard"
                label="Existing policy number"
                controlIdentifier="existing-policy-number"
                name={policyOutcomeFieldNames(person).EXISTING_POLICY_NUMBER}
                sx={{ width: "100%" }}
                defaultValue={
                  dataSet != null ? dataSet.existingPolicyNumber : ""
                }
                onFocus={(e) => handleInputOnFocus(e)}
              />
            </Section>
          </OneColumnStack>
          <OneColumnStack
            maxWidth="100%"
            sx={{
              display: "flex",
              justifyContent: "flex-start",
              flexDirection: "column",
              outline: "0px solid red",
            }}
          >
            <Section
              xs={4}
              mt={1}
              data-identifier="accept-upgrade"
              sx={{ display: showButton ? "flex" : "none" }}
            >
              <Button
                id="accept-upgrade-button"
                ariaLabel="accept upgrade button"
                variant="contained"
                color="primary"
                size="medium"
                onClick={handleOnSave}
                // type="submit"
                disabled={
                  !enableSaveButton ||
                  isRetrievePolicyExpired ||
                  disablePolicyUpgradeButton
                }
              >
                Save
              </Button>
            </Section>
            {errorState ? (
              <NotificationCard
                cardType={"error"}
                title=""
                alignIcon="flex-start"
                message={
                  "This is not an existing policy number. Please re-enter the policy number."
                }
              />
            ) : null}
            {successState ? (
              <NotificationCard
                cardType={"success"}
                title="Save confirmation"
                alignIcon="flex-start"
                message={
                  "Thank you, your assessment has been finalised and your policy will be amended."
                }
              />
            ) : null}
          </OneColumnStack>
        </Box>
      </Fade>
    </>
  );
}
